import React, { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { reviewApi, adminPaymentLabel } from "./api";
export default function EndReviewPanel({
  id,
  onEnded,
}: {
  id: string;
  onEnded: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<any>(null),
    [reason, setReason] = useState(""),
    [refund, setRefund] = useState("none"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [confirmed, setConfirmed] = useState(false),
    [key] = useState(() => crypto.randomUUID()),
    [attempt, setAttempt] = useState(0),
    [result, setResult] = useState("");
  useEffect(() => {
    if (!expanded) return;
    const c = new AbortController();
    setError("");
    reviewApi(`/${id}/termination`, "GET", undefined, c.signal)
      .then(d => {
        setData(d);
        setRefund(
          d.payments.some((p: any) => ["paid", "refunding"].includes(p.status))
            ? "full"
            : "none"
        );
      })
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      });
    return () => c.abort();
  }, [id, attempt, expanded]);
  const captured =
    data?.payments.filter((p: any) =>
      ["paid", "refunding", "refunded"].includes(p.status)
    ) || [];
  return (
    <details
      className="mt-4 border-top pt-3"
      onToggle={e => setExpanded(e.currentTarget.open)}
    >
      <summary>End review / refund</summary>
      <p className="small mt-2">
        This ends further work and messaging. The client and attorney receive an
        update; the record remains available. Explain the decision without
        adding confidential facts.
      </p>
      {error && (
        <Alert variant="danger">
          {error}{" "}
          <Button
            variant="link"
            disabled={busy}
            onClick={() => setAttempt(n => n + 1)}
          >
            Reload status
          </Button>
        </Alert>
      )}
      {result && <Alert variant="info">{result}</Alert>}
      {!data ? (
        <p role="status">Loading payment status…</p>
      ) : (
        <>
          <p>
            Payment:{" "}
            {adminPaymentLabel(data.review)?.text ||
              (captured.length
                ? "Captured payment — see ledger below"
                : "No captured payment")}
          </p>
          {captured.map((p: any) => (
            <p className="small" key={p.id}>
              ${(p.amount_cents / 100).toFixed(2)} captured · $
              {(p.refunded_cents / 100).toFixed(2)} refunded · {p.status}
            </p>
          ))}
          {data.review.closed_at ? (
            <>
              <p>
                <strong>Review ended.</strong> {data.review.closure_reason}
              </p>
              {data.payments.some((p: any) => p.status === "refunding") && (
                <Button
                  variant="outline-dark"
                  disabled={busy || !data.refundProviderAvailable}
                  onClick={async () => {
                    setBusy(true);
                    setError("");
                    try {
                      const d = await reviewApi(
                        `/${id}/retry-refund`,
                        "POST",
                        {}
                      );
                      setResult(
                        d.refundPending
                          ? "Refund confirmation is still pending. Staff reconciliation may be needed."
                          : "The refund is confirmed."
                      );
                      setAttempt(n => n + 1);
                      onEnded();
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Retry pending refund
                </Button>
              )}
            </>
          ) : (
            <Form
              onSubmit={async e => {
                e.preventDefault();
                if (!confirmed || busy) return;
                setBusy(true);
                setError("");
                try {
                  const d = await reviewApi(`/${id}/end-review`, "POST", {
                    version: data.review.version,
                    requestKey: key,
                    reason,
                    refund,
                  });
                  if (d.refundPending) {
                    setResult(
                      "The review ended. The refund is pending and will be retried automatically. Check the ledger before promising receipt."
                    );
                    setAttempt(n => n + 1);
                  }
                  onEnded();
                } catch (e: any) {
                  setError(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Form.Label htmlFor="end-review-reason">
                Explanation shown to the client
              </Form.Label>
              <Form.Control
                id="end-review-reason"
                as="textarea"
                rows={2}
                maxLength={2000}
                required
                value={reason}
                disabled={busy}
                onChange={e => {
                  setReason(e.target.value);
                  setConfirmed(false);
                }}
              />
              {captured.length > 0 && (
                <>
                  <Form.Label htmlFor="end-refund" className="mt-2">
                    Refund decision
                  </Form.Label>
                  <Form.Select
                    id="end-refund"
                    value={refund}
                    disabled={busy}
                    onChange={e => {
                      setRefund(e.target.value);
                      setConfirmed(false);
                    }}
                  >
                    <option value="full">
                      Refund remaining captured payment in full
                    </option>
                    <option value="none">
                      End review without an additional refund
                    </option>
                    <option value="already_refunded">
                      Confirm the ledger already shows a full refund
                    </option>
                  </Form.Select>
                </>
              )}
              {refund === "full" &&
                captured.some((p: any) => p.refunded_cents < p.amount_cents) &&
                !data.refundProviderAvailable && (
                  <Alert variant="warning" className="mt-2">
                    The payment provider is unavailable. Configure it or
                    reconcile the refund before ending this paid review.
                  </Alert>
                )}
              <Form.Check
                className="my-3"
                id="confirm-end-review"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                label={`I confirm ending this review${refund === "full" && captured.length ? " and issuing the remaining full refund" : ""}.`}
              />
              <Button
                type="submit"
                variant="outline-danger"
                disabled={busy || !confirmed || !reason.trim()}
              >
                {busy ? "Ending…" : "End review"}
              </Button>
            </Form>
          )}
        </>
      )}
    </details>
  );
}
